/**
 * As etapas da implantação e as contas do suporte.
 *
 * O checklist é DERIVADO: cada etapa é uma pergunta que o banco responde agora.
 * Guardar "etapa concluída" numa tabela criaria um segundo registro do mesmo
 * fato, e ele continuaria marcado depois que alguém apagasse a jornada. Um
 * checklist derivado se desmarca sozinho — e é justamente isso que se quer de
 * uma lista de implantação.
 */

import type {
    Chamado, EtapaDoOnboarding, Indicador, OnboardingDaClinica,
} from './tipos';

export interface RespostasDoOnboarding {
    temCadastro: boolean;
    temEndereco: boolean;
    temJornada: boolean;
    temLogin: boolean;
    temProcedimentos: boolean;
    temCanal: boolean;
    temAtendimento: boolean;
}

interface DefinicaoDeEtapa {
    id: keyof RespostasDoOnboarding;
    rotulo: string;
    descricao: string;
}

/* A ordem é a da implantação de verdade: sem cadastro não se emite documento,
   sem jornada não se mede ocupação, e o atendimento é o que prova que a clínica
   começou a usar. */
export const ETAPAS: DefinicaoDeEtapa[] = [
    { id: 'temCadastro', rotulo: 'Cadastro da clínica', descricao: 'Razão social, documento e e-mail comercial preenchidos' },
    { id: 'temEndereco', rotulo: 'Endereço da unidade', descricao: 'Sai em documento e em notificação ao paciente' },
    { id: 'temJornada', rotulo: 'Horários de funcionamento', descricao: 'É o denominador da ocupação nos relatórios' },
    { id: 'temLogin', rotulo: 'Alguém com login ativo', descricao: 'Membro cadastrado que já entrou pelo menos uma vez' },
    { id: 'temProcedimentos', rotulo: 'Procedimentos cadastrados', descricao: 'Sem catálogo não há preço nem agenda' },
    { id: 'temCanal', rotulo: 'Canal de mensagem pareado', descricao: 'É o que permite confirmar e lembrar consulta' },
    { id: 'temAtendimento', rotulo: 'Primeira consulta concluída', descricao: 'O sinal de que a clínica começou a usar' },
];

export function etapasDe(respostas: RespostasDoOnboarding): EtapaDoOnboarding[] {
    return ETAPAS.map((e) => ({
        id: e.id,
        rotulo: e.rotulo,
        descricao: e.descricao,
        concluida: respostas[e.id],
    }));
}

export function indicadoresDoOnboarding(clinicas: OnboardingDaClinica[]): Indicador[] {
    const completas = clinicas.filter((c) => c.concluidas === c.total);
    const paradas = clinicas.filter((c) => c.concluidas === 0);
    const media = clinicas.length
        ? Math.round((clinicas.reduce((s, c) => s + c.concluidas / c.total, 0) / clinicas.length) * 100)
        : 0;

    return [
        { id: 'implantando', rotulo: 'Em implantação', valor: String(clinicas.length - completas.length), apoio: `de ${clinicas.length} clínicas`, icone: 'rocket' },
        { id: 'completas', rotulo: 'Implantação completa', valor: String(completas.length), apoio: 'todas as etapas', tendencia: completas.length ? 'up' : 'neutral', icone: 'circle-check' },
        /* Zero etapa é diferente de "quase lá": é a clínica que assinou e não
           fez nada, e é onde o suporte tem mais a ganhar. */
        { id: 'paradas', rotulo: 'Sem nenhuma etapa', valor: String(paradas.length), apoio: 'assinaram e não começaram', tendencia: paradas.length ? 'down' : 'up', icone: 'triangle-alert' },
        { id: 'media', rotulo: 'Avanço médio', valor: `${media}%`, apoio: 'das etapas, na base inteira', icone: 'trending-up' },
    ];
}

/** Horas entre abertura e resolução; ausente enquanto o chamado está aberto. */
export function horasAteResolver(c: { abertoEm: string; resolvidoEm?: string }): number | undefined {
    if (!c.resolvidoEm) return undefined;
    return Math.round((new Date(c.resolvidoEm).getTime() - new Date(c.abertoEm).getTime()) / 3600000);
}

export function indicadoresDoSuporte(chamados: Chamado[]): Indicador[] {
    const abertos = chamados.filter((c) => c.situacao !== 'resolvido');
    const semDono = abertos.filter((c) => !c.responsavelNome);
    const resolvidos = chamados.filter((c) => c.situacao === 'resolvido' && c.horasAteResolver !== undefined);
    const media = resolvidos.length
        ? Math.round(resolvidos.reduce((s, c) => s + (c.horasAteResolver ?? 0), 0) / resolvidos.length)
        : undefined;

    return [
        { id: 'abertos', rotulo: 'Chamados abertos', valor: String(abertos.length), apoio: `${chamados.length} no total`, icone: 'headset' },
        {
            id: 'sem-dono', rotulo: 'Sem responsável', valor: String(semDono.length),
            apoio: semDono.length ? 'ninguém assumiu' : 'todos têm dono',
            tendencia: semDono.length ? 'down' : 'up', icone: 'user-x',
        },
        {
            id: 'alta', rotulo: 'Prioridade alta', valor: String(abertos.filter((c) => c.prioridade === 'alta').length),
            apoio: 'entre os abertos', icone: 'flame',
        },
        /* Sem nenhum resolvido não há média — e "0 h" seria lido como
           atendimento instantâneo. */
        {
            id: 'tempo', rotulo: 'Tempo médio de resolução',
            valor: media === undefined ? '—' : `${media} h`,
            apoio: media === undefined ? 'nenhum chamado resolvido ainda' : `${resolvidos.length} resolvidos`,
            icone: 'clock',
        },
    ];
}
